import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  AnalyticsTypeMap,
  SelectionDescriptionMap,
  canSelectMultipleFilters,
  DateTextMap,
  headToHeadStatType,
  LOCATION_MAP,
  FAVOURITES_VIEW,
  FIXTURES_VIEW,
  DATE_VIEW,
  STATS_VIEW,
  ODDS_VIEW,
  MARKETS_VIEW,
} from "../../../../constants";
import styles from "./FilterSelection.module.scss";
import TickIcon from "../../../../assets/icons/tick.svg";
import { sendAnalyticsEvent } from "../../../../lib/postMessage";
import { getDateFormat, handleDateFormat } from "../../../../lib";
import FilterHeadToHeadWarning from "../FilterHeadToHeadWarning";
import { getNumberOfMarkets } from "../../../../lib/api";
import { EditFavouritesButton } from "../EditFavouritesButton";
import { EditFavouritePopUp } from "../../../UpdateFavourites/components/EditFavouritePopUp";
import {
  DESELECT,
  FAVOURITES,
  FILTERS,
  SELECT,
} from "../../../../constants/analytics";

const FilterSelection = ({
  name,
  type,
  id,
  homeTeamName,
  awayTeamName,
  date,
  children,
  activeFilterView,
}) => {
  const [showH2HWarning, setShowH2HWarning] = useState(false);
  const { prefetchedFilters, storedFavourites } = useSelector(
    (state) => state.filter
  );
  const dispatch = useDispatch();
  const { activeView } = useSelector((state) => state.view);
  const isDateView = activeFilterView === DATE_VIEW;
  const isFixturesView = activeFilterView === FIXTURES_VIEW;
  const isFavouritesView = activeFilterView === FAVOURITES_VIEW;
  const isStatsView = activeFilterView === STATS_VIEW;
  const selectedFavourite = isFavouritesView
    ? storedFavourites.find((favourite) => favourite.id === id)
    : null;

  // Converted Date Formats
  const todaysDate = handleDateFormat();
  const fixtureDate = handleDateFormat(date);

  // FAVOURITES_VIEW is a special case where we want to get the type from the favourites object
  const selectionType = isFavouritesView
    ? storedFavourites.find((favourite) => favourite.id === id)[type]
    : type;

  // Check to see if this is a filter where you can make multiple selections
  const isMultipleSelection =
    canSelectMultipleFilters.includes(activeFilterView);
  const filterType = isFavouritesView
    ? [prefetchedFilters.favourites[type]]
    : prefetchedFilters[activeFilterView];
  let mergedFixtures = [];

  // Only merge the fixtures together if either nothing has been selected in the date view or both selections (“Today” and “Tomorrow”)
  // have been selected. Otherwise we should only be showing the fixtures on the relevant day.
  if (isFixturesView) {
    if (prefetchedFilters.date.length === 1) {
      mergedFixtures = filterType[prefetchedFilters.date[0]];
    } else {
      mergedFixtures = [...filterType["today"], ...filterType["tomorrow"]];
    }
  }

  const getNumberOfMarketSelections = async () => {
    const params = {
      basedOn: headToHeadStatType,
      date:
        prefetchedFilters.date.length === 1
          ? getDateFormat(prefetchedFilters.date[0])
          : null,
      fixtures:
        [
          ...prefetchedFilters.fixtures.today,
          ...prefetchedFilters.fixtures.tomorrow,
        ].join() || null,
      markets: prefetchedFilters.markets.join() || null,
    };
    const res = await getNumberOfMarkets(params);

    if (res.code === 0 && res.count === 0) {
      setShowH2HWarning(true);
    } else {
      setShowH2HWarning(false);
    }
  };

  useEffect(() => {
    if (isStatsView && type === headToHeadStatType) {
      getNumberOfMarketSelections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterView]);

  const filterContainsActiveSelection = isFixturesView
    ? mergedFixtures.includes(id)
    : filterType.includes(selectionType);

  const filterContainsActiveOddsSelection = Boolean(
    isFavouritesView &&
      type === ODDS_VIEW &&
      prefetchedFilters.favourites.odds === selectedFavourite.id
  );

  const filterContainsFavouriteMarketSelections = Boolean(
    isFavouritesView &&
      type === MARKETS_VIEW &&
      prefetchedFilters.favourites.markets === selectedFavourite.id
  );

  // Handle Fixture Selection differently
  const handleFixtureFilterSelection = () => {
    const analyticsEvent = {
      event: FILTERS,
      type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
      value: `${homeTeamName} v ${awayTeamName}`,
      location: LOCATION_MAP.get(activeView) || "",
    };
    // Split adding to the fixtures object into two manageable arrays today and tomorrow
    // To find the active selection, we combine both arrays together and look up the id of the fixture
    // We can remove any fixtures in today or tomorrow array if the date filter chosen is opposite by
    // removing that array from the object.

    // if an already active selection we should remove from the relevant fixture array here
    if (filterContainsActiveSelection) {
      dispatch({
        type: "REMOVE_FROM_FIXTURES_FILTER_SELECTIONS",
        payload: {
          filter: activeFilterView,
          date: fixtureDate === todaysDate ? "today" : "tomorrow", // Check if the date is today if so we from from today array otherwise we remove from tomorrow array
          fixture: id,
        },
      });
      sendAnalyticsEvent({ ...analyticsEvent, action: "Deselect" });
    } else {
      dispatch({
        type: "ADD_TO_FIXTURES_FILTER_SELECTIONS",
        payload: {
          filter: activeFilterView,
          date: fixtureDate === todaysDate ? "today" : "tomorrow", // Check if the date is today if so we add to a today array otherwise we add to a tomorrow array
          fixture: id,
        },
      });
      sendAnalyticsEvent({ ...analyticsEvent, action: "Select" });
    }
  };

  const handleFavouriteFilterSelection = () => {
    // add the selected favourite to the prefetchedFilters
    if (
      prefetchedFilters.favourites.markets !== selectedFavourite.id &&
      prefetchedFilters.favourites.odds !== selectedFavourite.id
    ) {
      dispatch({
        type: "ADD_FAVOURITE_SELECTIONS",
        payload: {
          id: selectedFavourite.id,
        },
      });
      sendAnalyticsEvent({
        event: FAVOURITES,
        type:
          selectedFavourite.type.charAt(0).toUpperCase() +
          selectedFavourite.type.slice(1),
        action: SELECT,
      });
    } else {
      dispatch({
        type: "REMOVE_FAVOURITE_SELECTIONS",
        payload: {
          id: selectedFavourite.id,
        },
      });
    }
  };

  const handleFilterSelection = () => {
    // We only close the entire filter if it is not a filter where you can make multiple selections such as stats
    if (!isMultipleSelection) {
      dispatch({
        type: "SET_ACTIVE_FILTER_VIEW",
        payload: "",
      });
    }

    // if we are on a filter that can make multiple selections we can add to that filter's array of selections
    // otherwise we just update the string value for a single selection
    if (isMultipleSelection) {
      // if an already active selection we should remove from the filter selections
      if (filterContainsActiveSelection) {
        // If both Today and Tomorrow are selected in the date filter, select fixtures across both day and
        // return to the date view and deselect either Today or Tomorrow it should clear the fixture(s)
        // selected on the date filter that was removed
        if (isDateView && prefetchedFilters.date.length === 2) {
          dispatch({
            type: "EMPTY_DATE_FIXTURES_FILTER_SELECTIONS",
            payload: {
              filter: "fixtures",
              date: type === "tomorrow" ? "tomorrow" : "today",
            },
          });
        }

        dispatch({
          type: "REMOVE_FROM_FILTER_SELECTIONS",
          payload: {
            filter: activeFilterView,
            type,
          },
        });
        sendAnalyticsEvent({
          event: FILTERS,
          type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
          action: DESELECT,
          value: name,
          location: LOCATION_MAP.get(activeView) || "",
        });
      } else {
        // Add to filter selections array
        dispatch({
          type: "ADD_TO_FILTER_SELECTIONS",
          payload: {
            filter: activeFilterView,
            type,
          },
        });
        sendAnalyticsEvent({
          event: FILTERS,
          type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
          action: SELECT,
          value: name,
          location: LOCATION_MAP.get(activeView) || "",
        });

        if (isDateView && !prefetchedFilters.date.length) {
          // If no date filters have been selected previously we should remove fixture(s) from fixtures
          // if the current date filter is not the same date as the fixtures stored
          dispatch({
            type: "EMPTY_DATE_FIXTURES_FILTER_SELECTIONS",
            payload: {
              filter: "fixtures",
              date: type === "tomorrow" ? "today" : "tomorrow",
            },
          });
        }
      }
    } else {
      dispatch({
        type: "TOGGLE_FILTER_SELECTION",
        payload: {
          filter: activeFilterView,
          type,
        },
      });
      sendAnalyticsEvent({
        event: FILTERS,
        type: AnalyticsTypeMap.get(activeFilterView) ?? activeFilterView,
        action: SELECT,
        value: name,
        location: LOCATION_MAP.get(activeView) || "",
      });
      // Finalise Filter Selections here to update the selections
      dispatch({
        type: "FINALISE_FILTER_SELECTIONS",
      });
    }
  };

  const selected = Boolean(
    filterType === selectionType ||
      filterContainsActiveSelection ||
      (isFavouritesView && filterContainsActiveOddsSelection) ||
      (isFavouritesView && filterContainsFavouriteMarketSelections) ||
      (!isMultipleSelection && filterType === selectionType)
  );

  const renderCheckBox = () => {
    if (isMultipleSelection || isFavouritesView) {
      return (
        <input
          className={styles.filterCheckbox}
          type="checkbox"
          aria-label={`${isFixturesView ? id : type}-checkbox`}
          name={`${isFixturesView ? id : type}-checkbox`}
          checked={selected}
          readOnly // Set as readOnly as the onChange handling happens when clicking on the whole row
        />
      );
    }

    if (selected) {
      return <img className={styles.filterIcon} src={TickIcon} alt="Tick" />;
    }

    return null;
  };
  const renderName = () => {
    if (isFixturesView) {
      return `${homeTeamName} v ${awayTeamName}`;
    }

    return name;
  };

  const renderDescription = () => {
    if (isFixturesView) {
      return `${DateTextMap.get(dayjs(date).format("YYYY-MM-DD"))} at ${dayjs(
        date
      ).format("HH:mm")}`;
    }
    if (children) {
      return children;
    }
    return SelectionDescriptionMap.get(type);
  };

  function handleSelectionClick() {
    if (showH2HWarning) {
      return () => {};
    }
    if (isFixturesView) {
      return handleFixtureFilterSelection();
    }
    if (isFavouritesView) {
      return handleFavouriteFilterSelection();
    }
    return handleFilterSelection();
  }

  return (
    <li
      className={`${styles.filterSelection} ${
        selected ? styles["filterSelection--active"] : ""
      }`}
      data-testid={`selection-${type}`}
    >
      <div
        role="presentation"
        className={styles.filterSelectionContent}
        onClick={() => handleSelectionClick()}
      >
        {isFixturesView ? (
          <div
            className={`${styles.filterLoading} ${
              isDateView ? styles["filterLoading--date"] : ""
            }`}
          >
            <div className={styles.filterLoadingText} />
            <div className={styles.filterLoadingText} />
          </div>
        ) : null}
        <div
          className={`${styles.filterName} ${
            showH2HWarning && styles["filterName--disabled"]
          }`}
        >
          <h4>{renderName()}</h4>
          <p className={styles.filterDescription}>{renderDescription()}</p>
          {showH2HWarning && <FilterHeadToHeadWarning />}
        </div>
        {renderCheckBox()}
      </div>
      {Boolean(isFavouritesView) && (
        <EditFavouritesButton>
          <EditFavouritePopUp favourite={id} />
        </EditFavouritesButton>
      )}
    </li>
  );
};

export default FilterSelection;
